import { SecureRoute, LoginCallback } from "@okta/okta-react";
import { Redirect } from "react-router-dom";
import UnifyContainer from "../core/UnifyContainer";
import { Suspense, useEffect, useState } from "react";
import { BrowserRouter as Router, Route } from "react-router-dom";
import LogoutCallback from "../../components/LogoutCallback/LogoutCallback";
import { AnalyticsProvider } from "../third-party-integration/amplitude";
import { useOktaAuth } from "@okta/okta-react";
import { AppLoader } from "../common/AppLoader";
//import {useSmartVersionCheck} from "../core/VersionCheck";
import { FFContextProvider } from "@harnessio/ff-react-client-sdk";
import {  APP_ENV, appUrlDiscovery, REACT_APP_HARNESS } from "../../constants";
import BackForwardListener from "../navigation/BackForwardListener";
import userProfileStore from "../core/UserProfileStore";
import { insertNewRelicError } from "../../util/newRelicApi";
import { useUserProfile } from "../../hooks/queries";
import {v4 as uuidv4} from 'uuid';
import { Button, BUTTON_VARIANTS} from 'cdk-radial';

const amplitudeKey = process.env.REACT_APP_AMPLITUDE_API_KEY;


export function ErrorHandler(error: any) {
  const [isErrorShow, setIsErrorShow] = useState<boolean>(false);
  const onClick = () => setIsErrorShow(true);
  const traceId = uuidv4();
  const errorData = JSON.stringify(error);
  insertNewRelicError(`UIOKTAERROR: ${traceId} Error: ${errorData}`, "");
  const { data, isLoading, isError } = useUserProfile();

  const storedErrorCount = localStorage.getItem('errorCount');
  const errorCount = storedErrorCount ? parseInt(storedErrorCount, 10) : 0;
 
  useEffect(() => {
    if (!isError && !isLoading) {
      insertNewRelicError(`UIOKTAERROR-USER: ${traceId} Error: ${errorData}`, data?.userProfile?.email || "");
    }
  }, [isError, isLoading, data, traceId, errorData]);

  useEffect(() => {
    if (errorCount < 2 && !errorData.includes("The JWT was issued in the future")) {
      for (const key in localStorage) {
        if (key !== 'errorCount') {
          localStorage.removeItem(key);
        }
      }
      insertNewRelicError(`UIOKTAERROR-USER: ${traceId} ${errorCount} Error: ${errorData}`, "");
      localStorage.setItem('errorCount', (errorCount + 1).toString()); 
      window.location.href = appUrlDiscovery(APP_ENV);
    }
  }, [errorCount, traceId, errorData]);

  return (
      <>
        {errorData.includes("The JWT was issued in the future") ?
          <>
          <div style={{ marginBottom: "10px", marginLeft: "10px" }} data-testid="error-detail-one">The application didn't launch correctly.</div>
            <div style={{ marginBottom: "10px", marginLeft: "10px" }} data-testid="error-detail-two">Please ensure that your system's date and time settings are set to automatic.</div></>
            :<>
            <div style={{ marginBottom: "10px", marginLeft: "10px" }} data-testid="error-detail-one">The application didn't launch correctly.</div>
            <div style={{ marginBottom: "10px", marginLeft: "10px" }} data-testid="error-detail-two">Please refresh the browser.</div>
            <div style={{ marginLeft: "10px" }} data-testid="error-detail-three">If the problem still persists, please clear your browser cache and cookies, and try again.</div>
          </>}
        <Button text="Click for more details" variant={BUTTON_VARIANTS.TEXT} onClick={onClick} data-testid="error-button" />
        {isErrorShow ? <div data-testid="error-detail-four">{errorData}</div> : null}</>);

}


export function LoginCallbackComponent(props: any) {
  return (
    <LoginCallback
      {...props}
      loadingElement={<AppLoader loaderText="CDK Unify" />}
      errorComponent= {ErrorHandler}
    />
  );
}

const harnessTargeIdentifiers = (authState: any, enterpriseId: string) => {
  let email = authState?.accessToken?.claims?.cdksid || "";
  // Fall back to a default identifier until Okta auth resolves; the FF SDK errors on an empty identifier.
  email = email.replace(/[^a-zA-Z0-9@.]/g, "") || "UNIFY";
  return {
    identifier: email,
    name: email,
    attributes: {
      enterpriseId: enterpriseId,
      email: email,
    },
  };
};

const OktaLoginRoute = (() => {
  const { authState } = useOktaAuth();
  const userId = authState?.accessToken?.claims?.uid || "";

  // Prefer the IAM profile (already loaded into the store by UnifyContainer); fall
  // back to the OKTA claim while it loads or if the request fails.
  const iamUserProfile = userProfileStore((state) => state.iamUserProfile);
  const enterpriseId =
    iamUserProfile?.fullUser?.enterprise?.id ||
    authState?.accessToken?.claims?.enterpriseId ||
    "";

  /*useSmartVersionCheck({
    periodicCheckInterval: 30 * 60 * 1000, // 30 minutes
    checkOnVisibility: true,
    checkOnFocus: false,
    onUpdateAvailable: (version) => {
      console.log('Update available:', version);
      console.log("new version of app is released ")
      // Optional: Send analytics event
      // trackEvent('update_available', { version });
    },
  });*/


  return (
    <>
      <AnalyticsProvider
          apiKey={amplitudeKey}
          userId={userId}
          sessionReplay={true}
        >
          
          <FFContextProvider
                async={true}
                apiKey={REACT_APP_HARNESS}
                target={harnessTargeIdentifiers(authState, enterpriseId)}
                options={{
                  streamEnabled: true,
                }}
              >
          <Router>
            <Suspense
              fallback={<AppLoader loaderText="CDK Unify" />}
            ></Suspense>
            <SecureRoute
              exact
              path="/"
              render={() => <Redirect to={"/unify"} />}
            />
            <Route exact path="/er-unify" render={() => <Redirect to={`/unify`} />}/>
            <Route exact path="/applications" render={() => <Redirect to={`unify/applications`} />}/>
            <Route exact path="/myUnify" render={() => <Redirect to={`unify/myUnify`} />}/>
            <Route exact path="/workflows" render={() => <Redirect to={`unify/workflows`} />}/>
            <Route path="/login/callback" component={LoginCallbackComponent} />
            <Route exact path="/logout/callback" component={LogoutCallback} />
            <SecureRoute path="/unify" component={UnifyContainer} />
            <BackForwardListener></BackForwardListener>
          </Router>
          </FFContextProvider>
        </AnalyticsProvider>
    </>
  );
});

export default OktaLoginRoute;
